import { useMutation } from '@apollo/client';
import type { OptionalMetadata, SecurityGroupsInput } from '@communities/community/domain';
import { CREATE_COMMUNITY } from '@communities/community/graphql/createCommunity';
import { GET_COMMUNITIES_IN_GROUP } from '@communities/community/graphql/getCommunitiesInGroup';
import { GENERATE_COMMUNITY_PROMPTS, GET_COMMUNITY_PROMPTS, UPDATE_PROMPT_PROD_VERSION } from '@communities/prompt-management/graphql';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { track } from '@core/logging';
import { useCallback, useState } from 'react';

export interface CreateCommunityInput {
  name: string;
  optionalMetadata?: OptionalMetadata;
  securityGroups?: SecurityGroupsInput;
}

export function useCreateCommunity() {
  const [errors, setErrors] = useState<DomainError[] | null>(null);

  // Community creation mutation
  const [createCommunityMutation, { loading: isCreating }] = useMutation(CREATE_COMMUNITY);

  // Prompt generation mutation
  const [generatePromptsMutation, { loading: isGeneratingPrompt }] = useMutation(GENERATE_COMMUNITY_PROMPTS);

  // Update prod prompt version mutation
  const [updatePromptProdVersionMutation, { loading: isUpdatingPrompt }] = useMutation(UPDATE_PROMPT_PROD_VERSION);

  const resetErrors = useCallback(() => {
    setErrors(null);
  }, []);

  const createCommunity = useCallback(
    async (communityGroupId: string, input: CreateCommunityInput): Promise<string | null> => {
      try {
        track('CreateCommunityInitiated', {
          communityGroupId,
          communityName: input.name,
        });

        // Step 1: Create the community
        const result = await createCommunityMutation({
          variables: { input: { ...input, communityGroupId } },
          refetchQueries: communityGroupId ? [{ query: GET_COMMUNITIES_IN_GROUP, variables: { communityGroupId } }] : [],
          awaitRefetchQueries: true,
        });

        const communityId = result.data?.createCommunity?.id;

        if (communityId) {
          track('CreateCommunitySucceeded', {
            communityGroupId,
            communityId,
          });

          // Step 2: Generate prompts
          const promptResult = await generatePromptsMutation({
            variables: {
              communityId,
              input: { labelFeedback: [] },
            },
            errorPolicy: 'ignore',
          });

          const version = promptResult.data?.generateCommunityPrompts?.version;

          // Step 3: Update prod prompt version if we have a version
          if (version) {
            await updatePromptProdVersionMutation({
              variables: {
                communityId,
                input: { version },
              },
              refetchQueries: [{ query: GET_COMMUNITY_PROMPTS, variables: { communityId } }],
              awaitRefetchQueries: true,
              errorPolicy: 'ignore',
            });
          }
        }

        return communityId;
      } catch (error) {
        track('CreateCommunityFailed', {
          communityGroupId,
          errorMessage: error instanceof Error ? error.message : 'Unknown error',
        });

        setErrors(transformErrors(error));

        return null;
      }
    },
    [createCommunityMutation, generatePromptsMutation, updatePromptProdVersionMutation]
  );

  return {
    createCommunity,
    isCreating,
    isGenerating: isGeneratingPrompt || isUpdatingPrompt,
    errors,
    resetErrors,
  };
}

export default useCreateCommunity;
